import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { KeyRound, ShieldCheck, ShieldOff, Save, Copy } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

/**
 * Per-admin Security page.
 *
 * Two sections:
 *   1. Change password — old + new + confirm. Backend enforces 8+ chars.
 *   2. Two-factor authentication — set up TOTP secret via Google Authenticator
 *      / Authy / 1Password. Three flows:
 *        a) Not enabled    → "Set up 2FA" → shows QR + manual key → confirm code
 *        b) Setup pending  → still need to enter the code
 *        c) Enabled        → green badge + "Disable" form (re-prompts password)
 */
export default function Security() {
  const { user, refreshUser } = useAuth();
  return (
    <div className="admin-section max-w-2xl">
      <Toaster richColors position="top-right" />
      <h1 className="font-display text-3xl text-[#5D2C8F] mb-1">Security</h1>
      <p className="text-sm text-[#6E6074] mb-6">Manage your password and two-factor authentication.</p>

      <PasswordCard />
      <TwoFactorCard user={user} onChanged={refreshUser} />
    </div>
  );
}

function PasswordCard() {
  const [old, setOld] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (next.length < 8) { toast.error("New password must be 8+ characters"); return; }
    setBusy(true);
    try {
      await api.post("/admin/me/password", { old_password: old, new_password: next, confirm });
      setOld(""); setNext(""); setConfirm("");
      toast.success("Password updated ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password change failed");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="admin-surface p-5 mb-5" data-testid="security-password-card">
      <h2 className="font-display text-xl text-[#5D2C8F] flex items-center gap-2 mb-1"><KeyRound size={18} /> Change password</h2>
      <p className="text-xs text-[#6E6074] mb-4">Minimum 8 characters. After saving, you stay signed in on this device.</p>
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <Field label="Current password"><input type="password" value={old} onChange={(e) => setOld(e.target.value)} required className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="security-old-password" /></Field>
        <Field label="New password"><input type="password" value={next} onChange={(e) => setNext(e.target.value)} required className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="security-new-password" /></Field>
        <Field label="Confirm new"><input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2" data-testid="security-confirm-password" /></Field>
      </div>
      <button type="submit" disabled={busy} className="sac-btn-primary mt-4 text-sm" data-testid="security-password-save"><Save size={14} /> {busy ? "Saving…" : "Save new password"}</button>
    </form>
  );
}

function TwoFactorCard({ user, onChanged }) {
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = !!user?.twofa_enabled;
  const enforced = !!user?.enforce_2fa;

  // Owner has reset_2fa flow on the AdminUsers page; keep this card self-contained.
  useEffect(() => { setSetupData(null); setCode(""); }, [enabled]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/me/2fa/setup");
      setSetupData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start setup"); }
    finally { setBusy(false); }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter all 6 digits"); return; }
    setBusy(true);
    try {
      await api.post("/admin/me/2fa/enable", { code });
      toast.success("Two-factor authentication enabled ✓");
      setSetupData(null);
      setCode("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't enable"); }
    finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    if (!disablePassword) { toast.error("Enter your password to confirm"); return; }
    setBusy(true);
    try {
      await api.post("/admin/me/2fa/disable", { password: disablePassword });
      toast.success("Two-factor authentication disabled");
      setDisablePassword("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't disable"); }
    finally { setBusy(false); }
  };

  return (
    <div className="admin-surface p-5" data-testid="security-2fa-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h2 className="font-display text-xl text-[#5D2C8F] flex items-center gap-2"><ShieldCheck size={18} /> Two-factor authentication</h2>
          <p className="text-xs text-[#6E6074] mt-1 max-w-prose">Adds a 6-digit code from <strong>Google Authenticator</strong>, <strong>Authy</strong>, or <strong>1Password</strong> to your login. Even if someone steals your password they can't get in without your phone.</p>
        </div>
        {enabled ? (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-[#8EC9C7]/30 text-[#348469] whitespace-nowrap" data-testid="security-2fa-status-enabled"><ShieldCheck size={12} /> Enabled</span>
        ) : (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-[#FAF6EE] text-[#6E6074] whitespace-nowrap" data-testid="security-2fa-status-disabled"><ShieldOff size={12} /> Off</span>
        )}
      </div>

      {enforced && !enabled && (
        <div className="bg-[#FAEFE0] border border-[#E8C37A] text-[#8F6B1F] rounded-lg p-3 text-xs mt-3" data-testid="security-2fa-enforced-warning">
          <strong>The owner requires you to enable 2FA.</strong> Please set it up below — it takes 60 seconds.
        </div>
      )}

      {!enabled && !setupData && (
        <button onClick={startSetup} disabled={busy} className="sac-btn-primary text-sm mt-3" data-testid="security-2fa-setup-btn">
          {busy ? "Loading…" : "Set up 2FA"}
        </button>
      )}

      {!enabled && setupData && (
        <form onSubmit={confirmEnable} className="mt-4 space-y-3 bg-[#FAF6EE] border border-[#E5E0D8] rounded-lg p-4" data-testid="security-2fa-setup-panel">
          <div className="grid sm:grid-cols-[200px_1fr] gap-4 items-start">
            <img src={setupData.qr_data_url} alt="2FA QR code" className="w-[200px] h-[200px] rounded-lg border border-[#E5E0D8] bg-white" data-testid="security-2fa-qr" />
            <div className="text-xs text-[#3A2A47] space-y-2">
              <p>1. Open Google Authenticator (or Authy / 1Password) on your phone.</p>
              <p>2. Tap <strong>+</strong> → <strong>Scan a QR code</strong> and scan the image to the left.</p>
              <p>3. Can't scan? Enter this key manually instead:</p>
              <div className="flex items-center gap-2">
                <code className="bg-white border border-[#E5E0D8] rounded px-2 py-1.5 flex-1 font-mono text-[#5D2C8F] break-all" data-testid="security-2fa-secret">{setupData.secret}</code>
                <button type="button" onClick={() => { navigator.clipboard.writeText(setupData.secret); toast.success("Copied"); }} className="text-[#5D2C8F]" aria-label="Copy" data-testid="security-2fa-copy-secret"><Copy size={14} /></button>
              </div>
              <p>4. Type the 6-digit code below to confirm.</p>
            </div>
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">6-digit code from your app</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-2.5 text-center text-2xl tracking-[0.4em]"
              placeholder="123 456"
              data-testid="security-2fa-code"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setSetupData(null); setCode(""); }} className="text-sm text-[#A685BD] px-3" data-testid="security-2fa-cancel">Cancel</button>
            <button type="submit" disabled={busy || code.length !== 6} className="sac-btn-primary text-sm disabled:opacity-50" data-testid="security-2fa-confirm">{busy ? "Verifying…" : "Confirm and enable"}</button>
          </div>
        </form>
      )}

      {enabled && (
        <form onSubmit={disable} className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end" data-testid="security-2fa-disable-form">
          <Field label="Confirm password to turn off 2FA">
            <input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-3 py-2 text-sm" data-testid="security-2fa-disable-password" />
          </Field>
          <button type="submit" disabled={busy} className="text-sm text-[#A685BD] hover:text-[#5D2C8F] border border-[#E5E0D8] rounded-lg px-4 py-2 hover:bg-[#FAF6EE]" data-testid="security-2fa-disable-btn">Disable 2FA</button>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[#5D2C8F] block mb-1">{label}</span>
      {children}
    </label>
  );
}
